import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentAccessToken,
  getCurrentUserId,
  getDealApplicationId,
  getDealBorrowerId,
  getHasPreferredLanguageOverride,
  getLoanId,
  getUserRole,
  setDealBorrowerId,
  setPreferredLanguage,
} from "features/authSlice";
import { useGetBorrowerDetailsQuery, useGetBorrowerIdentifiersQuery, useGetBorrowerPersonalInformationQuery, useUpdateUserPreferredLanguageMutation } from "pages/dashboard/home/dashboardService";
import { DEFAULT_LANGUAGE } from "utilities/Constant";
import { extractDealServicePreferredLanguage, hasExplicitUserLanguageSelection, normalizeToAppLanguage, toUserDbPreferredLanguage } from "utilities/getPreferredLanguage";
import { RegistrationRoleType } from "pages/registration/registrationService";

const isCoBorrowerRole = (userRole?: string): boolean => {
  return userRole === RegistrationRoleType.SpousalCoBorrower ||
    userRole === RegistrationRoleType.NonSpousalCoBorrower ||
    userRole === RegistrationRoleType.CoBorrower;
};

export default function useLoginPreferredLanguageSync() {
  const dispatch = useDispatch();
  const accessToken = useSelector(getCurrentAccessToken);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const loanId = useSelector(getLoanId);
  const userRole = useSelector(getUserRole);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);

  const isAuthenticated = Boolean(accessToken);
  const hasApplicationContext = Boolean(dealApplicationId);

  const { data: borrowerIdentifiers } = useGetBorrowerIdentifiersQuery(
    { loanId, applicationId: dealApplicationId },
    { skip: !isAuthenticated || !hasApplicationContext || !loanId },
  );
  const resolvedDealBorrowerId = isCoBorrowerRole(userRole)
    ? borrowerIdentifiers?.coBorrowerId ?? dealBorrowerId
    : borrowerIdentifiers?.borrowerId ?? dealBorrowerId;

  const {
    data: dealServicePersonalInformation,
    isError: isDealServiceError,
  } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: resolvedDealBorrowerId || "" },
    { skip: !isAuthenticated || !hasApplicationContext || !resolvedDealBorrowerId },
  );

  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !isAuthenticated || !userId,
  });
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();

  useEffect(() => {
    if (!isAuthenticated || !hasApplicationContext) {
      return;
    }

    if (dealServicePersonalInformation?.dealBorrowerId) {
      dispatch(setDealBorrowerId(dealServicePersonalInformation.dealBorrowerId));
    }

    if (hasPreferredLanguageOverride || hasExplicitUserLanguageSelection()) {
      return;
    }

    if (isDealServiceError) {
      dispatch(setPreferredLanguage(normalizeToAppLanguage(userProfile?.preferredLanguage ?? DEFAULT_LANGUAGE)));
      return;
    }

    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);
    if (!dealServiceLanguage) {
      dispatch(setPreferredLanguage(normalizeToAppLanguage(userProfile?.preferredLanguage ?? DEFAULT_LANGUAGE)));
      return;
    }

    const normalizedDealServiceLanguage = normalizeToAppLanguage(dealServiceLanguage);
    dispatch(setPreferredLanguage(normalizedDealServiceLanguage));

    if (userId) {
      void updateUserPreferredLanguage({
        userId,
        preferredLanguage: toUserDbPreferredLanguage(normalizedDealServiceLanguage),
      });
    }
  }, [
    dispatch,
    isAuthenticated,
    hasApplicationContext,
    dealServicePersonalInformation,
    isDealServiceError,
    hasPreferredLanguageOverride,
    userProfile?.preferredLanguage,
    userId,
    updateUserPreferredLanguage,
  ]);

  return {
    isAuthenticated,
    hasApplicationContext,
    dealApplicationId,
  };
}
